import { z } from 'zod'

/**
 * Creates a regex pattern for Latin character validation
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns RegExp for Latin character validation
 */
export const createLatinCharacterRegex = (allowedSymbols?: string[]): RegExp => {
  // Default symbols: space, dash, apostrophe, comma, period, ampersand, braces, brackets, colon, semicolon, underscore
  // Includes Latin diacritics: é, è, ê, ë, à, á, â, ã, ä, ï, ì, î, ò, ó, ô, õ, ö, ù, ú, û, ü, ý, ÿ, ñ, ç, ß
  // Includes additional dashes: hyphen (‐), en dash (–), em dash (—)
  // Note: {}()[] need to be escaped in regex character classes
  const defaultSymbols = " \\-',.&\\{\\}\\(\\)\\[\\]:;_‐–—"

  if (!allowedSymbols || allowedSymbols.length === 0) {
    return new RegExp(`^[a-zA-ZÀ-ÿß0-9${defaultSymbols}]+$`)
  }

  // Handle custom symbols when explicitly provided - include both default and custom symbols
  const escapedSymbols = allowedSymbols
    .map(symbol => symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('')

  return new RegExp(`^[a-zA-ZÀ-ÿß0-9${defaultSymbols}${escapedSymbols}]+$`)
}

/**
 * Creates a Zod schema for Latin character validation
 * @param errorMessage - The error message to display when validation fails
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns Zod schema that validates Latin characters
 */
export const createLatinCharacterSchema = (errorMessage: string, allowedSymbols?: string[]) => {
  return z.string().refine(
    value => {
      if (!value) {return true} // Allow empty strings
      const regex = createLatinCharacterRegex(allowedSymbols)
      return regex.test(value)
    },
    {
      message: errorMessage
    }
  )
}

/**
 * Creates a Zod schema for required Latin character validation with optional constraints
 * @param errorMessage - The error message to display when validation fails
 * @param options - Optional constraints like min/max length
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns Zod schema that validates required Latin characters
 */
export const createRequiredLatinCharacterSchema = (
  errorMessage: string,
  options?: { min?: number; max?: number },
  allowedSymbols?: string[]
) => {
  let schema = z.string().min(1)

  if (options?.min) {
    schema = schema.min(options.min)
  }
  if (options?.max) {
    schema = schema.max(options.max)
  }

  return schema.refine(
    value => {
      const regex = createLatinCharacterRegex(allowedSymbols)
      return regex.test(value)
    },
    {
      message: errorMessage
    }
  )
}

/**
 * Creates a Zod schema for optional Latin character validation with optional constraints
 * @param errorMessage - The error message to display when validation fails
 * @param options - Optional constraints like max length
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns Zod schema that validates optional Latin characters
 */
export const createOptionalLatinCharacterSchema = (
  errorMessage: string,
  options?: { max?: number },
  allowedSymbols?: string[]
) => {
  let schema = z.string()

  if (options?.max) {
    schema = schema.max(options.max)
  }

  return schema.optional().refine(
    value => {
      if (!value) {return true} // Allow empty optional fields
      const regex = createLatinCharacterRegex(allowedSymbols)
      return regex.test(value)
    },
    {
      message: errorMessage
    }
  )
}

/**
 * Validates if a string contains only Latin characters and allowed symbols
 * @param value - The string to validate
 * @param errorMessage - The error message to display when validation fails
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns null if valid, error message if invalid
 */
export const validateLatinCharacters = (
  value: string,
  errorMessage: string,
  allowedSymbols?: string[]
): string | null => {
  if (!value) {return null} // Allow empty strings

  try {
    const schema = createLatinCharacterSchema(errorMessage, allowedSymbols)
    schema.parse(value)
    return null
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0]?.message || errorMessage
    }
    return errorMessage
  }
}

/**
 * Checks if a string contains only Latin characters and allowed symbols
 * @param value - The string to check
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns true if the string is Latin-only, false otherwise
 */
export const isLatinOnly = (value: string, allowedSymbols?: string[]): boolean => {
  if (!value) {return true}
  const regex = createLatinCharacterRegex(allowedSymbols)
  return regex.test(value)
}
